/* eslint-disable camelcase */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { LAYOUT_TYPE } from '~/gridv2/constants';
import CaretIcon2 from '~/icons/components/CaretIcon2';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import {
  useFilterAvailability,
  useStagedFilters,
} from '../../context/PendingFiltersContext';
import { getFacetRBKey } from '../../utils/filters';

import FilterOptionsRenderer from './FilterOptions/FilterOptionsRenderer';
import { StyledFilterCount } from './QuickFilter';

const DynamicFormattedMessage = FormattedMessage;

const DropdownContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const DropdownHeader = styled.button<{ $isExpanded: boolean }>`
  width: 100%;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0px 16px;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.palette.shadow30};
  border-radius: 4px;
  border-bottom-left-radius: ${({ $isExpanded }) =>
    $isExpanded ? '0px' : '4px'};
  border-bottom-right-radius: ${({ $isExpanded }) =>
    $isExpanded ? '0px' : '4px'};
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  line-height: 24px;
  text-align: left;
`;

const DropdownPanel = styled.div<{ $isExpanded: boolean }>`
  overflow: hidden;
  transition:
    max-height 0.3s ease,
    opacity 0.3s ease,
    padding 0.3s ease,
    border-color 0.3s ease;
  // animation is not smooth, temp fix for now, will harden in animation ticket
  max-height: ${({ $isExpanded }) => ($isExpanded ? '100%' : '0')};
  opacity: ${({ $isExpanded }) => ($isExpanded ? '1' : '0')};
  padding: ${({ $isExpanded }) => ($isExpanded ? '24px 16px' : '0 16px')};
  border: 1px solid transparent;
  border-color: ${({ $isExpanded, theme }) =>
    $isExpanded ? theme.colors.palette.shadow30 : 'transparent'};
  border-top: none;
  border-radius: 0 0 4px 4px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
`;

const StyledCaretIcon2 = styled(CaretIcon2)`
  transform: ${({ $isExpanded }) =>
    $isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 0.2s;
`;

const StyledTextAndCountContainer = styled.div<{
  $allDisabled?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 8px;
  opacity: ${({ $allDisabled }) => ($allDisabled ? 0.32 : 1)};
`;

const CountDivider = styled.div`
  width: 2px;
  height: 2px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.gridV2FloatingControlsText};
  flex-shrink: 0;
  margin-top: 2px;
`;

const FilterDropdown = ({ name, display_name, options, isActive = false }) => {
  const { toggleFilter, isFilterSelected } = useStagedFilters();
  const { isFacetOptionDisabled } = useFilterAvailability();
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const triggerExpand = useCallback(() => {
    setIsExpanded(true);
    // Defer scrollIntoView by one frame so the state update has been committed
    // and the element has its new position before we scroll to it.
    requestAnimationFrame(() => {
      if (containerRef.current) {
        containerRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    });
  }, []);

  const handleClick = () => {
    if (!isExpanded) {
      triggerExpand();
    } else {
      setIsExpanded(false);
    }
  };

  useEffect(() => {
    if (isActive) {
      triggerExpand();
    }
  }, [isActive, triggerExpand]);

  const filterCount =
    options?.filter((option) =>
      isFilterSelected({
        facetName: name,
        facetOption: option.value,
      })
    )?.length || 0;

  const hasOneEnabledOption = options?.some(
    (option) => !isFacetOptionDisabled(name, option.value)
  );
  const allOptionsDisabled = options?.length > 0 && !hasOneEnabledOption;

  return (
    <DropdownContainer ref={containerRef}>
      <DropdownHeader
        $isExpanded={isExpanded}
        onClick={handleClick}
        data-autotag={
          isExpanded ? `filter-${name}-collapse` : `filter-${name}-expand`
        }
      >
        <StyledTextAndCountContainer $allDisabled={allOptionsDisabled}>
          <DynamicFormattedMessage
            id={`grid_dynamic.${getFacetRBKey(name)}`}
            defaultMessage={display_name}
          />
          {allOptionsDisabled ? (
            <>
              <CountDivider />
              <FormattedMessage
                id="grid_static.no_matches"
                defaultMessage="No Matches"
              />
            </>
          ) : (
            filterCount > 0 && (
              <>
                <CountDivider />
                <StyledFilterCount>{filterCount}</StyledFilterCount>
              </>
            )
          )}
        </StyledTextAndCountContainer>
        <StyledCaretIcon2 $isExpanded={isExpanded} />
      </DropdownHeader>

      <DropdownPanel $isExpanded={isExpanded}>
        <FilterOptionsRenderer
          facetName={name}
          options={options}
          onClick={(facetValue) => {
            toggleFilter({
              facetName: name,
              facetOption: facetValue,
            });
          }}
          isSelected={(facetValue) =>
            isFilterSelected({
              facetName: name,
              facetOption: facetValue,
            })
          }
          isDisabled={(facetValue) => isFacetOptionDisabled(name, facetValue)}
          layoutType={LAYOUT_TYPE.FILTER_PANEL}
        />
      </DropdownPanel>
    </DropdownContainer>
  );
};

FilterDropdown.propTypes = {
  display_name: PropTypes.string,
  isActive: PropTypes.bool,
  name: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
      count: PropTypes.number,
    })
  ),
};

export default FilterDropdown;
